import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaCheck, FaEye, FaRegEdit } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { BsThreeDotsVertical } from "react-icons/bs";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PageHeading from "../PageHeading";
import ConfirmActionModal from "../ConfirmDeleteModal";
import ExportOptions from "../ExportOptions";
import SmartDropdown from "../Shared/SmartDropdown";
import axios from "axios";
import TableSkeleton from "../TableSkeleton";
import HospitalViewAdmissionModal from "./HospitalViewAdmissionModal";

const api = axios.create({
  baseURL: "https://api.muktihospital.com/api/hospital",
  headers: {
    "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
  },
});

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const HospitalAdmittedPatient = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [admissions, setAdmissions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [activeDropdownId, setActiveDropdownId] = useState(null);
  const [loading, setLoading] = useState(false);
  const itemsPerPage = 20;
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const actionButtonClass =
    "size-7 rounded-full text-sm flex items-center justify-center transition-all duration-300";
  const DEBUG = process.env.NODE_ENV !== "production";

  // Define skeleton columns to match table structure
  const skeletonColumns = [
    { width: 20, height: 20, isCircle: false, className: "print-hidden-in-print" }, // Checkbox
    { width: 50, height: 20 }, // SL No
    { width: 100, height: 20 }, // Reg No
    { width: 150, height: 20 }, // Patient Name
    { width: 120, height: 20 }, // Admission Date
    { width: 150, height: 20 }, // Consultant
    { width: 80, height: 20 }, // Bed No
    { width: 100, height: 20 }, // Billing Type
    { width: 100, height: 20 }, // Status
    { width: 50, height: 20, className: "print-hidden-in-print" }, // Actions
  ];

  // Create refs object for all admissions for SmartDropdown positioning
  const triggerRefs = useRef({});

  // Clean up unused refs
  useEffect(() => {
    Object.keys(triggerRefs.current).forEach((key) => {
      if (!admissions.some((admission) => admission.id === key)) {
        delete triggerRefs.current[key];
      }
    });
  }, [admissions]);

  const fetchAdmissions = useCallback(
    async (page, query) => {
      setLoading(true);
      toast.dismiss("no-admissions-found-toast");
      toast.dismiss("failed-to-load-admissions-toast");

      try {
        const dynamicLimit = query ? "0" : itemsPerPage;
        const dynamicPage = query ? 1 : page;

        const response = await api.get("/admission", {
          params: {
            page: dynamicPage,
            limit: dynamicLimit,
            search: query,
          },
        });
        
        const { admissions: rawAdmissions, pagination } = response.data.data;

        if (!Array.isArray(rawAdmissions)) {
          throw new Error("Invalid response format: Expected array of admissions");
        }

        // 1. Map the data
        const mappedAdmissions = rawAdmissions.map((admission) => ({
          id: admission.id,
          regNo: admission.regNo,
          patientName: admission.patientName,
          admissionDate: admission.admissionDate,
          consultantName: admission.consultantName,
          bedNo: admission.bedNo,
          billingType: admission.billingType,
          status: admission.status,
          specialNo: admission.specialNo,
          serviceType: admission.serviceType,
          fatherName: admission.fatherName,
          motherName: admission.motherName,
          relativeName: admission.relativeName,
          careOf: admission.careOf,
          age: admission.age,
          sex: admission.sex,
          religion: admission.religion,
          address: admission.address,
          dateOfBirth: admission.dateOfBirth,
          phoneNumber: admission.phoneNumber,
          admissionTime: admission.admissionTime,
          relativeMobileNumber: admission.relativeMobileNumber,
          relationship: admission.relationship,
          agentName: admission.agentName,
          wardCabin: admission.wardCabin,
          primaryDiagnosis: admission.primaryDiagnosis,
          employeeCode: admission.employeeCode,
          employeeName: admission.employeeName,
          dischargeCode: admission.dischargeCode,
        }));

        if (query) {
           const lowerQ = query.toLowerCase().trim();
           
           mappedAdmissions.sort((a, b) => {
              const regA = (a.regNo || "").toLowerCase();
              const regB = (b.regNo || "").toLowerCase();

              const isExactA = regA === lowerQ || regA.endsWith(`-${lowerQ}`) || regA === lowerQ;
              const isExactB = regB === lowerQ || regB.endsWith(`-${lowerQ}`) || regB === lowerQ;

              if (isExactA && !isExactB) return -1;
              if (!isExactA && isExactB) return 1;

              const hasRegA = regA.includes(lowerQ);
              const hasRegB = regB.includes(lowerQ);

              if (hasRegA && !hasRegB) return -1;
              if (!hasRegA && hasRegB) return 1;

              return 0; 
           });
        }

        const total = pagination?.totalRecords || rawAdmissions.length; 
        const calculatedTotalPages = Math.ceil(total / itemsPerPage) || 1;


        setAdmissions(mappedAdmissions);
        setTotalPages(calculatedTotalPages);
        setTotalRecords(total);

        if (mappedAdmissions.length === 0 && query) {
          toast.info("No admissions found matching your search.", { toastId: "no-admissions-found-toast", autoClose: 2000 });
        }
        
        if (mappedAdmissions.length === 0 && page > 1 && calculatedTotalPages < page) {
          setCurrentPage(calculatedTotalPages);
        } else if (mappedAdmissions.length === 0 && total === 0 && page > 1) {
            setCurrentPage(1);
        }

      } catch (err) {
        if (DEBUG) console.error("DEBUG: fetchAdmissions error", err);
        toast.error(
          `Failed to load admissions: ${err.response?.data?.error || err.message}`,
          { toastId: "failed-to-load-admissions-toast", autoClose: 3000 }
        );
        setAdmissions([]);
        setTotalPages(1);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Handle deletion
  const handleDelete = useCallback(
    async (admissionIds) => {
      const ids = Array.isArray(admissionIds) ? admissionIds : [admissionIds];
      if (!ids.length) {
        toast.error("No admissions selected for deletion.", { autoClose: 2000 });
        return;
      }
      setLoading(true);
      toast.dismiss();

      try {
        await Promise.all(
          ids.map(async (id) => {
            await api.delete(`/admission/delete/${id}`);
          })
        );

        toast.success(
          `${ids.length} admission${ids.length > 1 ? "s" : ""} deleted successfully.`,
          { autoClose: 2000 }
        );

        setSelectedRows([]);
        setPendingDeleteId(null);
        setDeleteModalOpen(false);

        // Re-fetch data for the current page after deletion.
        await fetchAdmissions(currentPage, debouncedSearchQuery);

      } catch (err) {
        if (DEBUG) console.error("DEBUG: handleDelete error", err);
        toast.error(
          `Failed to delete admission(s): ${err.response?.data?.error || err.message}`,
          { autoClose: 3000 }
        );
      } finally {
        setLoading(false);
      }
    },
    [currentPage, debouncedSearchQuery, fetchAdmissions]
  );

  // Open deletion confirmation
  const openDeleteConfirmation = useCallback((admissionId) => {
    if (!admissionId) {
      toast.error("No admission selected for deletion.", { autoClose: 2000 });
      return;
    }
    setPendingDeleteId(admissionId);
    setDeleteModalOpen(true);
  }, []);

  // Handle edit
  const handleEdit = useCallback(
    (admission) => {
      if (!admission?.id) {
        toast.error("Invalid admission ID for editing.", { autoClose: 2000 });
        return;
      }
      const editAdmissionData = {
        id: admission.id,
        regNo: admission.regNo,
        specialNo: admission.specialNo,
        serviceType: admission.serviceType,
        patientName: admission.patientName,
        fatherName: admission.fatherName,
        motherName: admission.motherName,
        relativeName: admission.relativeName,
        careOf: admission.careOf,
        age: admission.age,
        sex: admission.sex,
        religion: admission.religion,
        address: admission.address,
        dateOfBirth: admission.dateOfBirth,
        phoneNumber: admission.phoneNumber,
        admissionDate: admission.admissionDate,
        admissionTime: admission.admissionTime,
        relativeMobileNumber: admission.relativeMobileNumber,
        relationship: admission.relationship,
        consultantName: admission.consultantName,
        agentName: admission.agentName,
        wardCabin: admission.wardCabin,
        bedNo: admission.bedNo,
        primaryDiagnosis: admission.primaryDiagnosis,
        employeeCode: admission.employeeCode,
        employeeName: admission.employeeName,
        dischargeCode: admission.dischargeCode,
        billingType: admission.billingType,
        status: admission.status,
      };
      navigate("/hospital-admission", {
        state: { editAdmissionData },
      });
    },
    [navigate]
  );

  // Handle release (status update)
  const handleRelease = useCallback(
    async (admissionId) => {
      if (!admissionId) {
        toast.error("No admission selected for release.", { autoClose: 2000 });
        return;
      }
      setLoading(true);
      toast.dismiss(); // Dismiss all current toasts

      try {
        // if (DEBUG) console.log("DEBUG: handleRelease", { admissionId });
        await api.put(`/admission/edit/${admissionId}`, {
          status: "Released",
          dischargeCode: `R${Date.now()}`,
        });
        toast.success("Admission status updated to Released.", { autoClose: 2000 });
        await fetchAdmissions(currentPage, debouncedSearchQuery);
      } catch (err) {
        if (DEBUG) console.error("DEBUG: handleRelease error", err);
        toast.error(
          `Failed to update admission status: ${err.response?.data?.error || err.message}`,
          { autoClose: 3000 }
        );
      } finally {
        setLoading(false);
      }
      setActiveDropdownId(null); // Close dropdown after action
    },
    [currentPage, debouncedSearchQuery, fetchAdmissions]
  );

  // Handle single delete (from dropdown)
  const handleSingleDelete = useCallback(
    (admissionId) => {
      openDeleteConfirmation(admissionId);
    },
    [openDeleteConfirmation]
  );

  const handleView = useCallback((admission) => {
    setSelectedAdmission(admission);
    setViewModalOpen(true);
    setActiveDropdownId(null);
  }, []);

  // Effect to fetch admissions when page or debounced search query changes
  useEffect(() => {
    fetchAdmissions(currentPage, debouncedSearchQuery);
  }, [currentPage, debouncedSearchQuery, fetchAdmissions]);

  // Reset page to 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery]);

  const handlePageChange = useCallback(
    (page) => {
      if (page > 0 && page <= totalPages) {
        setCurrentPage(page);
        setSelectedRows([]);
      }
    },
    [totalPages, DEBUG]
  );

  const handleSelectAll = useCallback(
    (e) => {
      const validIds = admissions
        .filter((admission) => admission.id != null)
        .map((admission) => admission.id);
      setSelectedRows(e.target.checked ? validIds : []);
    },
    [admissions, DEBUG]
  );

  const handleRowSelect = useCallback((admissionId) => {
    if (admissionId == null) {
      if (DEBUG)
        console.warn("DEBUG: handleRowSelect: Invalid admissionId", { admissionId });
      return;
    }
    setSelectedRows((prev) =>
      prev.includes(admissionId)
        ? prev.filter((id) => id !== admissionId)
        : [...prev, admissionId]
    );
  }, [DEBUG]);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  // Get status label and styling
  const getStatusLabel = useCallback((admission) => {
    switch (admission.status) {
      case "Released":
        return { label: "Released", className: "bg-green-100 text-green-700" };
      case "Admitted":
        return { label: "Admitted", className: "bg-orange-100 text-orange-600" };
      default:
        return { label: admission.status || "Admitted", className: "bg-gray-100 text-gray-600" };
    }
  }, []);

  const headers = [
    "SL No",
    "Reg No",
    "Patient Name",
    "Admission Date",
    "Consultant",
    "Bed No",
    "Billing Type",
    "Status",
  ];

const dataMapper = useCallback((admission) => {
    
    const formatDate = (isoString) => {
      if (!isoString) return "-";
      const date = new Date(isoString);
      
      if (isNaN(date.getTime())) return "-";
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      
      return `${month}/${day}/${year}`; 
    };

    return [
      (currentPage - 1) * itemsPerPage + admissions.indexOf(admission) + 1,
      admission.regNo || "-",
      admission.patientName || "-",
      
      formatDate(admission.admissionDate), 
      
      admission.consultantName || "-",
      admission.bedNo || "-",
      admission.billingType || "-",
      admission.status || "Admitted",
    ].join("\t");
    
  }, [currentPage, itemsPerPage, admissions]);

  const breadcrumbs = useMemo(
    () => [
      { label: "Dashboard", url: "/" },
      { label: "Hospital Office"},
      { label: "Admitted Patients" },
    ],
    []
  );

  const allVisibleSelected = useMemo(
    () =>
      admissions.length > 0 &&
      admissions.every((admission) => admission.id && selectedRows.includes(admission.id)),
    [admissions, selectedRows]
  );

  return (
    <div>
      <ToastContainer position="top-right" className="print-hidden" />
      <div className="print-hidden">
        <PageHeading title="Hospital Admitted Patients" breadcrumbs={breadcrumbs} />
      </div>
      <div className="bg-white rounded-lg shadow-sm">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Admitted Patient List
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => openDeleteConfirmation(selectedRows)}
                className={`${actionButtonClass} bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white`}
                aria-label="Delete selected admissions"
                disabled={loading}
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by reg no, patient name, phone, bed no..." 
              value={searchQuery}
              onChange={handleSearchChange}
              aria-label="Search admissions by reg no or patient name"
            />
            <div className="flex items-center justify-between w-full gap-3">
              <button
                onClick={() => navigate("/hospital-admission")}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
                aria-label="Admit new NICU patient"
                disabled={loading}
              >
                Admit Patient
              </button>
              <ExportOptions
                data={admissions}
                dropdownOpen={dropdownOpen}
                toggleDropdown={toggleDropdown}
                title="Hospital Admitted Patient List"
                headers={headers}
                dataMapper={dataMapper}
              />
            </div>
          </div>
        </div>
        <style>
          {`
            @media print {
              .print-hidden, .print-hidden-in-print {
                display: none !important;
              }
              .print-table {
                width: 100%;
                border-collapse: collapse;
              }
              .print-table th, .print-table td {
                border: 1px solid #000;
                padding: 8px;
                text-align: left;
                font-size: 12px;
              }
              .print-table th {
                background-color: #f0f0f0;
                font-weight: bold;
              }
              .print-table tbody tr:nth-child(even) {
                background-color: #f9f9f9;
              }
            }
          `}
        </style>
        <div className="overflow-x-auto">
          <div className="print-wrapper">
            <table
              className="min-w-[1440px] w-full divide-y divide-gray-200 print-table"
              role="table"
            >
              <thead>
                <tr className="bg-[#FBFBFD]">
                  <th className="text-left print-hidden-in-print" scope="col">
                    <div className="relative left-3 -top-[2px]">
                      <input
                        type="checkbox"
                        id="selectAll"
                        className="hidden peer print-hidden"
                        onChange={handleSelectAll}
                        checked={allVisibleSelected}
                        disabled={admissions.length === 0 || loading}
                        aria-label="Select all admissions on this page"
                      />
                      <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                        <FaCheck className="text-slate-100" />
                      </span>
                      <label
                        htmlFor="selectAll"
                        className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                      ></label>
                    </div>
                  </th>
                  {headers.map((header, idx) => (
                    <th
                      key={idx}
                      className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3"
                      scope="col"
                    >
                      {header}
                    </th>
                  ))}
                  <th
                    className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print"
                    scope="col"
                  >
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton rowCount={itemsPerPage} columns={skeletonColumns} />
                ) : admissions.length === 0 ? (
                  <tr className="print-hidden">
                    <td
                      colSpan={9}
                      className="text-center py-5 text-gray-500"
                      role="cell"
                    >
                      {searchQuery
                        ? "No admissions found matching your search criteria."
                        : "No admissions available."}
                    </td>
                  </tr>
                ) : (
                  admissions.map((admission, index) => {
                    const statusStyles = getStatusLabel(admission);
                    return (
                      <tr
                        key={admission.id}
                        className="border-b border-M-text-color/10 hover:bg-gray-100 border-collapse"
                        role="row"
                      >
                        <td
                          className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden-in-print"
                          role="cell"
                        >
                          <div className="relative left-3 -top-[2px]">
                            <input
                              type="checkbox"
                              id={`admission-${index}`}
                              className="hidden peer"
                              checked={selectedRows.includes(admission.id)}
                              onChange={() => handleRowSelect(admission.id)}
                              aria-label={`Select admission ${admission.regNo || "unknown"}`}
                              disabled={loading}
                            />
                            <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                              <FaCheck className="text-slate-100" />
                            </span>
                            <label
                              htmlFor={`admission-${index}`}
                              className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                            ></label>
                          </div>
                        </td>
                        <td
                          className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                          role="cell"
                        >
                          {(currentPage - 1) * itemsPerPage + index + 1}
                        </td>
                        <td
                          className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                          role="cell"
                        >
                          {admission.regNo || "-"}
                        </td>
                        <td
                          className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                          role="cell"
                        >
                          {admission.patientName || "-"}
                        </td>
                        <td
                          className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                          role="cell"
                        >
                          {admission.admissionDate
                            ? new Date(admission.admissionDate).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              }).replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$1/$2/$3")
                            : "-"}
                        </td>
                        <td
                          className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                          role="cell"
                        >
                          {admission.consultantName || "-"}
                        </td>
                        <td
                          className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                          role="cell"
                        >
                          {admission.bedNo || "-"}
                        </td>
                        <td
                          className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                          role="cell"
                        >
                          {admission.billingType || "-"}
                        </td>
                        <td
                          className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3"
                          role="cell"
                        >
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${statusStyles.className}`}
                          >
                            {statusStyles.label}
                          </span>
                        </td>
                        <td
                          className="p-3 text-left relative print-hidden-in-print"
                          role="cell"
                        >
                          <button
                            ref={(el) => {
                              if (admission.id) {
                                if (!triggerRefs.current[admission.id]) {
                                  triggerRefs.current[admission.id] = { current: null };
                                }
                                triggerRefs.current[admission.id].current = el;
                              }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdownId(
                                activeDropdownId === admission.id ? null : admission.id
                              );
                            }}
                            className="size-7 rounded-full bg-gray-100 text-gray-600 text-sm inline-flex items-center justify-center hover:bg-gray-200 transition-all duration-300"
                            aria-label={`Open actions for admission ${admission.regNo || "unknown"}`}
                            disabled={loading} // Disable during loading
                          >
                            <BsThreeDotsVertical />
                          </button>
                          <SmartDropdown
                            isOpen={activeDropdownId === admission.id}
                            triggerRef={triggerRefs.current[admission.id] || { current: null }}
                            onClose={() => setActiveDropdownId(null)}
                            options={{ width: 150, align: "right" }}
                          >
                            <div>
                              <button onClick={() => handleView(admission)} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
                                <FaEye className="text-blue-500" /> View Details
                              </button>
                              <button
                                onClick={() => handleEdit(admission)}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <FaRegEdit className="text-M-primary-color" /> Edit
                              </button>
                              <button
                                onClick={() => handleSingleDelete(admission.id)}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <RiDeleteBin6Line className="text-red-600" /> Delete
                              </button>
                              {admission.status !== "Released" && (
                                <button
                                  onClick={() => handleRelease(admission.id)}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                >
                                  <FaCheck className="text-green-600" /> Released
                                </button>
                              )}
                            </div>
                          </SmartDropdown>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <div>
             {totalPages > 1 && !loading && (
            <div className="p-4 flex justify-between items-center print-hidden">
              <p className="text-sm text-gray-600">
                Records: {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, totalRecords)} of{" "}
                {totalRecords}
              </p>
              <nav aria-label="Pagination">
                <ul className="inline-flex items-center border border-M-text-color/20 rounded-md overflow-hidden">
                  <li>
                    <button
                      className={`table-pagination-item ${
                        currentPage === 1 ? "disabled" : ""
                      }`}
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      aria-label="Previous page"
                      aria-disabled={currentPage === 1}
                    >
                      <MdKeyboardArrowLeft />
                    </button>
                  </li>
                  {totalPages <= 10 ? (
                      [...Array(totalPages)].map((_, i) => (
                        <li key={i}>
                          <button
                            className={`table-pagination-item ${
                              currentPage === i + 1
                                ? "bg-M-primary-color text-white"
                                : ""
                            }`}
                            onClick={() => handlePageChange(i + 1)}
                            aria-label={`Go to page ${i + 1}`}
                            aria-current={currentPage === i + 1 ? "page" : undefined}
                          >
                            {i + 1}
                          </button>
                        </li>
                      ))
                    ) : (
                      (() => {
                        const pages = [];
                        const firstPages = 5;
                        const lastPages = 3;

                        for (let i = 1; i <= Math.min(firstPages, totalPages); i++) {
                          pages.push(
                            <li key={i}>
                              <button
                                className={`table-pagination-item ${
                                  currentPage === i
                                    ? "bg-M-primary-color text-white"
                                    : ""
                                }`}
                                onClick={() => handlePageChange(i)}
                                aria-label={`Go to page ${i}`}
                                aria-current={currentPage === i ? "page" : undefined}
                              >
                                {i}
                              </button>
                            </li>
                          );
                        }

                        if (totalPages > firstPages + lastPages) {
                          pages.push(
                            <li key="ellipsis" className="table-pagination-item">
                              ...
                            </li>
                          );
                        }

                        for (
                          let i = Math.max(firstPages + 1, totalPages - lastPages + 1);
                          i <= totalPages;
                          i++
                        ) {
                          pages.push(
                            <li key={i}>
                              <button
                                className={`table-pagination-item ${
                                  currentPage === i
                                    ? "bg-M-primary-color text-white"
                                    : ""
                                }`}
                                onClick={() => handlePageChange(i)}
                                aria-label={`Go to page ${i}`}
                                aria-current={currentPage === i ? "page" : undefined}
                              >
                                {i}
                              </button>
                            </li>
                          );
                        }
                        return pages;
                      })()
                    )}
                  <li>
                    <button
                      className={`table-pagination-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      aria-label="Next page"
                      aria-disabled={currentPage === totalPages}
                    >
                      <MdKeyboardArrowRight />
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
          </div>
        </div>
      </div>
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setPendingDeleteId(null);
        }}
        onConfirm={() => handleDelete(pendingDeleteId ? [pendingDeleteId] : selectedRows)}
        ids={pendingDeleteId ? [pendingDeleteId] : selectedRows}
        entityName="admission"
        title="Confirm Delete"
        confirmText="Delete"
        cancelText="Cancel"
        message={
          pendingDeleteId
            ? "Do you really want to delete this admission?"
            : selectedRows.length === 1
            ? "Do you really want to delete this admission?"
            : `Do you really want to delete ${selectedRows.length} admissions?`
        }
      />
      <HospitalViewAdmissionModal 
        isOpen={viewModalOpen} 
        onClose={() => setViewModalOpen(false)} 
        admission={selectedAdmission} 
        onEdit={handleEdit}
        onDelete={handleSingleDelete} 
      />
    </div>
  );
};

export default HospitalAdmittedPatient;